import { Model } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { MirageServer, MockServer } from '@@/api/mock-server/server.js'
import { createErrorResponse } from '@@/api/mock-server/utils.js'
import {
  getCurrentTimestamp,
  getDateTimeTimestamp,
  getTodayDateTimeISO,
  getTodayDateTimeISODate,
} from '@/utils/date/getter.js'
import { formatDate } from '@/utils/date/conversion.js'
import { subtractToDate } from '@/utils/date/calculation.js'
import { PROVET_PAY_PAYMENTS_FIELDS_PARAM_KEY } from '@/constants/reports/provetPayPaymentReport.js'

type SavedFilterData = {
  id: string
  created_at: string
  created_by: number
  created_by_name: string
  departments: number[]
  filters: Record<string, unknown>
  is_own: boolean
  is_predefined: boolean
  name: string
  object_type: string
  updated_at: string
}

export const model = Model as ModelDefinition<SavedFilterData>

/**
 * Store saved filters in localStorage to persist across browser reloads.
 * Storage key: `pvc-mirage-saved-filters`
 */
const STORAGE_KEY = 'pvc-mirage-saved-filters'

function getFiltersFromStorage(): SavedFilterData[] {
  if (typeof window === 'undefined') {
    return []
  }

  const stored = localStorage.getItem(STORAGE_KEY)
  if (!stored) {
    return []
  }

  try {
    return JSON.parse(stored)
  } catch {
    return []
  }
}

function setFiltersToStorage(filters: SavedFilterData[]): void {
  if (typeof window === 'undefined') {
    return
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
}

function generateId(): string {
  return String(getCurrentTimestamp())
}

export function routes(server: MockServer) {
  // GET /reports_v2/saved_filters - List saved filters
  server.get(
    '/reports_v2/saved_filters',
    function (this: MirageServer, schema, request) {
      const user = schema.first('user')

      if (!user) {
        return createErrorResponse({
          errors: [{ detail: 'User not authenticated' }],
          status: 403,
        })
      }

      const params = request.queryParams
      const objectTypeFilter = params['filter[object_type]']

      let filters = getFiltersFromStorage()

      // Filter by object_type if specified
      if (objectTypeFilter) {
        filters = filters.filter((f) => f.object_type === objectTypeFilter)
      }

      // Sort by created_at descending (newest first)
      filters.sort(
        (a, b) =>
          getDateTimeTimestamp(b.created_at) -
          getDateTimeTimestamp(a.created_at),
      )

      // Mark which filters are owned by the current user
      const currentUserId = user.id ? parseInt(user.id) : 0
      const filtersWithOwnership = filters.map((f) => ({
        ...f,
        is_own: f.created_by === currentUserId,
      }))

      return {
        data: filtersWithOwnership.map((filter) => ({
          id: filter.id,
          attributes: {
            name: filter.name,
            object_type: filter.object_type,
            filters: filter.filters,
            departments: filter.departments,
            created_by_name: filter.created_by_name,
            is_own: filter.is_own,
            is_predefined: filter.is_predefined ?? false,
            created_at: filter.created_at,
            updated_at: filter.updated_at,
          },
          type: 'SavedFilter',
        })),
      }
    },
  )

  // POST /reports_v2/saved_filters/ - Create a saved filter
  server.post(
    '/reports_v2/saved_filters',
    function (this: MirageServer, schema, request) {
      const user = schema.first('user')

      if (!user) {
        return createErrorResponse({
          errors: [{ detail: 'User not authenticated' }],
          status: 403,
        })
      }

      const body = JSON.parse(request.requestBody)
      const attributes = body?.data?.attributes

      if (!attributes?.name || !attributes?.object_type) {
        return createErrorResponse({
          errors: [{ detail: 'Name and object_type are required' }],
          status: 400,
        })
      }

      const now = getTodayDateTimeISO()
      const currentUserId = user.id ? parseInt(user.id) : 0
      const userName =
        `${user.attrs.first_name || ''} ${user.attrs.last_name || ''}`.trim()

      const newFilter: SavedFilterData = {
        id: generateId(),
        created_at: now,
        created_by: currentUserId,
        created_by_name: userName || 'Unknown User',
        departments: attributes.departments || [],
        filters: attributes.filters || {},
        is_own: true,
        is_predefined: false,
        name: attributes.name,
        object_type: attributes.object_type,
        updated_at: now,
      }

      const filters = getFiltersFromStorage()
      filters.push(newFilter)
      setFiltersToStorage(filters)

      return {
        data: {
          id: newFilter.id,
          attributes: {
            name: newFilter.name,
            object_type: newFilter.object_type,
            filters: newFilter.filters,
            departments: newFilter.departments,
            created_by_name: newFilter.created_by_name,
            is_own: newFilter.is_own,
            is_predefined: newFilter.is_predefined,
            created_at: newFilter.created_at,
            updated_at: newFilter.updated_at,
          },
          type: 'SavedFilter',
        },
      }
    },
  )

  // GET /reports_v2/saved_filters/:id/ - Get a specific saved filter
  server.get(
    '/reports_v2/saved_filters/:id/',
    function (this: MirageServer, schema, request) {
      const user = schema.first('user')

      if (!user) {
        return createErrorResponse({
          errors: [{ detail: 'User not authenticated' }],
          status: 403,
        })
      }

      const filterId = request.params.id
      const filters = getFiltersFromStorage()
      const filter = filters.find((f) => f.id === filterId)

      if (!filter) {
        return createErrorResponse({
          errors: [{ detail: 'Saved filter not found' }],
          status: 404,
        })
      }

      const currentUserId = user.id ? parseInt(user.id) : 0

      return {
        data: {
          id: filter.id,
          attributes: {
            name: filter.name,
            object_type: filter.object_type,
            filters: filter.filters,
            departments: filter.departments,
            created_by_name: filter.created_by_name,
            is_own: filter.created_by === currentUserId,
            is_predefined: filter.is_predefined ?? false,
            created_at: filter.created_at,
            updated_at: filter.updated_at,
          },
          type: 'SavedFilter',
        },
      }
    },
  )

  // PATCH /reports_v2/saved_filters/:id/ - Update a saved filter
  server.patch(
    '/reports_v2/saved_filters/:id/',
    function (this: MirageServer, schema, request) {
      const user = schema.first('user')

      if (!user) {
        return createErrorResponse({
          errors: [{ detail: 'User not authenticated' }],
          status: 403,
        })
      }

      const filterId = request.params.id
      const filters = getFiltersFromStorage()
      const filterIndex = filters.findIndex((f) => f.id === filterId)

      if (filterIndex === -1) {
        return createErrorResponse({
          errors: [{ detail: 'Saved filter not found' }],
          status: 404,
        })
      }

      const filter = filters[filterIndex]
      const currentUserId = user.id ? parseInt(user.id) : 0

      // Only owner can update (simplified - in real backend, admins can too)
      if (filter.created_by !== currentUserId) {
        return createErrorResponse({
          errors: [
            { detail: 'You do not have permission to update this filter' },
          ],
          status: 403,
        })
      }

      const body = JSON.parse(request.requestBody)
      const attributes = body?.data?.attributes || {}

      // Update allowed fields
      if (attributes.name !== undefined) {
        filter.name = attributes.name
      }
      if (attributes.filters !== undefined) {
        filter.filters = attributes.filters
      }
      if (attributes.departments !== undefined) {
        filter.departments = attributes.departments
      }

      filter.updated_at = getTodayDateTimeISO()

      filters[filterIndex] = filter
      setFiltersToStorage(filters)

      return {
        data: {
          id: filter.id,
          attributes: {
            name: filter.name,
            object_type: filter.object_type,
            filters: filter.filters,
            departments: filter.departments,
            created_by_name: filter.created_by_name,
            is_own: true,
            is_predefined: filter.is_predefined ?? false,
            created_at: filter.created_at,
            updated_at: filter.updated_at,
          },
          type: 'SavedFilter',
        },
      }
    },
  )

  // DELETE /reports_v2/saved_filters/:id/ - Delete a saved filter
  server.delete(
    '/reports_v2/saved_filters/:id/',
    function (this: MirageServer, schema, request) {
      const user = schema.first('user')

      if (!user) {
        return createErrorResponse({
          errors: [{ detail: 'User not authenticated' }],
          status: 403,
        })
      }

      const filterId = request.params.id
      const filters = getFiltersFromStorage()
      const filterIndex = filters.findIndex((f) => f.id === filterId)

      if (filterIndex === -1) {
        return createErrorResponse({
          errors: [{ detail: 'Saved filter not found' }],
          status: 404,
        })
      }

      const filter = filters[filterIndex]
      const currentUserId = user.id ? parseInt(user.id) : 0

      // Only owner can delete (simplified - in real backend, admins can too)
      if (filter.created_by !== currentUserId) {
        return createErrorResponse({
          errors: [
            { detail: 'You do not have permission to delete this filter' },
          ],
          status: 403,
        })
      }

      filters.splice(filterIndex, 1)
      setFiltersToStorage(filters)

      // Return 204 No Content
      return new Response(null, { status: 204 })
    },
  )
}

export function seeds(_server: MockServer) {
  // Seed some example saved filters for testing
  const existingFilters = getFiltersFromStorage()

  // Only seed if no filters exist
  if (existingFilters.length > 0) {
    return
  }

  const now = getTodayDateTimeISO()

  const today = getTodayDateTimeISODate()
  const oneMonthAgo = formatDate(
    subtractToDate(today, { days: 30 }),
    'yyyy-MM-dd',
  )

  const sampleFilters: SavedFilterData[] = [
    {
      id: '1',
      created_at: now,
      created_by: 1,
      created_by_name: 'Ada Lovelace',
      departments: [],
      filters: {
        filters: {
          paymentStatus: ['unpaid'],
          invoiceStatus: ['finalized'],
        },
        columnVisibility: {},
        sorting: [],
      },
      is_own: true,
      is_predefined: false,
      name: 'Unpaid Invoices',
      object_type: 'invoice_v2',
      updated_at: now,
    },
    {
      id: '2',
      created_at: now,
      created_by: 1,
      created_by_name: 'Ada Lovelace',
      departments: [],
      filters: {
        filters: {
          invoiceStatus: ['finalized'],
        },
        columnVisibility: {},
        sorting: [{ id: 'invoice_date', desc: true }],
      },
      is_own: true,
      is_predefined: false,
      name: 'This Month',
      object_type: 'invoice_v2',
      updated_at: now,
    },
    {
      id: '3',
      created_at: now,
      created_by: 1,
      created_by_name: 'Ada Lovelace',
      departments: [],
      filters: {
        filters: {
          invoiceStatus: ['finalized'],
        },
        columnVisibility: {},
        sorting: [{ id: 'invoice_date', desc: true }],
      },
      is_own: true,
      is_predefined: false,
      name: 'This Month',
      object_type: 'invoice_v2',
      updated_at: now,
    },
    {
      id: '4',
      created_at: now,
      created_by: 1,
      created_by_name: 'Ada Lovelace',
      departments: [],
      filters: {
        filters: {
          invoiceStatus: ['finalized'],
        },
        columnVisibility: {},
        sorting: [{ id: 'invoice_date', desc: true }],
      },
      is_own: true,
      is_predefined: false,
      name: 'This Month',
      object_type: 'invoice_v2',
      updated_at: now,
    },
    {
      id: '5',
      created_at: now,
      created_by: 1,
      created_by_name: 'Ada Lovelace',
      departments: [],
      filters: {
        filters: {
          invoiceStatus: ['finalized'],
        },
        columnVisibility: {},
        sorting: [{ id: 'invoice_date', desc: true }],
      },
      is_own: true,
      is_predefined: false,
      name: 'This Month This Month. This Month This Month',
      object_type: 'invoice_v2',
      updated_at: now,
    },
    {
      id: 'month-end-reconciliation',
      created_at: now,
      created_by: 1,
      created_by_name: '',
      departments: [],
      filters: {
        'filter[paid_out.in]': 'Yes',
        'filter[payment_date_ts_utc.gte]': oneMonthAgo,
        'filter[payment_date_ts_utc.lte]': today,
        [PROVET_PAY_PAYMENTS_FIELDS_PARAM_KEY]:
          'payment_date_ts_utc,payout_date_ts_utc,account_description,related_invoice,client,payment_amount,nordhealth_fee,paid_out',
        group_by: 'payout_date_ts_utc',
      },
      is_own: false,
      is_predefined: true,
      name: 'Month-End Reconciliation',
      object_type: 'provet_pay_payments_report_v1',
      updated_at: now,
    },
    {
      id: 'fee-breakdown-cost-analysis',
      created_at: now,
      created_by: 1,
      created_by_name: '',
      departments: [],
      filters: {
        'filter[result.in]': 'Success',
        'filter[payment_status.in]': 'Payment',
        'filter[payment_date_ts_utc.gte]': oneMonthAgo,
        'filter[payment_date_ts_utc.lte]': today,
        [PROVET_PAY_PAYMENTS_FIELDS_PARAM_KEY]:
          'payment_date_ts_utc,account_description,payment_method,payment_amount,nordhealth_fee,payout_date_ts_utc',
        group_by: 'payment_method',
      },
      is_own: false,
      is_predefined: true,
      name: 'Fee Breakdown & Cost Analysis',
      object_type: 'provet_pay_payments_report_v1',
      updated_at: now,
    },
    {
      id: 'failed-refused-payments',
      created_at: now,
      created_by: 1,
      created_by_name: '',
      departments: [],
      filters: {
        'filter[result.in]': 'Fail',
        'filter[payment_status.in]': 'Payment',
        [PROVET_PAY_PAYMENTS_FIELDS_PARAM_KEY]:
          'payment_date_ts_utc,payment_method,terminal,account_description,related_invoice,client,payment_amount',
        group_by: 'payment_method',
      },
      is_own: false,
      is_predefined: true,
      name: 'Failed & Refused Payments',
      object_type: 'provet_pay_payments_report_v1',
      updated_at: now,
    },
    {
      id: 'disputes-chargebacks',
      created_at: now,
      created_by: 1,
      created_by_name: '',
      departments: [],
      filters: {
        'filter[payment_status.in]': 'Dispute,Chargeback,ChargebackReversed',
        [PROVET_PAY_PAYMENTS_FIELDS_PARAM_KEY]:
          'payment_date_ts_utc,result,payment_status,client,related_invoice,payment_method,payment_amount,nordhealth_fee,paid_out',
      },
      is_own: false,
      is_predefined: true,
      name: 'Disputes & Chargebacks',
      object_type: 'provet_pay_payments_report_v1',
      updated_at: now,
    },
    {
      id: 'recent-refunds',
      created_at: now,
      created_by: 1,
      created_by_name: '',
      departments: [],
      filters: {
        'filter[payment_status.in]': 'Refund',
        'filter[payment_date_ts_utc.gte]': oneMonthAgo,
        'filter[payment_date_ts_utc.lte]': today,
        [PROVET_PAY_PAYMENTS_FIELDS_PARAM_KEY]:
          'payment_date_ts_utc,result,account_description,related_invoice,client,payment_amount,paid_out,payout_date_ts_utc',
      },
      is_own: false,
      is_predefined: true,
      name: 'Recent Refunds',
      object_type: 'provet_pay_payments_report_v1',
      updated_at: now,
    },
    {
      id: 'none-predefined',
      created_at: now,
      created_by: 1,
      created_by_name: '',
      departments: [],
      filters: {
        'filter[payment_date_ts_utc.gte]': oneMonthAgo,
        'filter[payment_date_ts_utc.lte]': today,
        'filter[payment_status.in]': 'Chargeback',
        [PROVET_PAY_PAYMENTS_FIELDS_PARAM_KEY]:
          'payment_date_ts_utc,result,account_description,related_invoice,client,payment_amount,paid_out,payout_date_ts_utc',
      },
      is_own: false,
      is_predefined: false,
      name: 'Not predefined',
      object_type: 'provet_pay_payments_report_v1',
      updated_at: now,
    },
  ]

  setFiltersToStorage(sampleFilters)
}
